import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'

import { BookableSlot } from 'src/app/features/bookable-slot/bookable-slot'
import moment from 'moment-mini'
import { combineLatest, Observable, of } from 'rxjs'
import { map, take, withLatestFrom } from 'rxjs/operators'

import { DatePipe } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

import { ScheduleSlot } from './slot'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'
import { translationSelectors } from 'src/app/features/translation/translation.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Product } from 'src/app/features/product/product'
import { groupBy } from 'src/app/shared/until'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-item-schedule',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss'],
  providers: [DatePipe]
})
export class SerieProposalItemScheduleComponent implements OnInit {
  @Input() bookableSlots$: Observable<BookableSlot[]>
  @Input() latestBookableSlots$: Observable<BookableSlot[]> = of([])
  @Input() targetedProductsIds: any[]
  @Input() config: {
    slotInterval: number
    openTime: string
    closeTime: string
    scheduleHeaderWidth: number
  }

  @Output() onClickBookableSlot: EventEmitter<BookableSlot> = new EventEmitter()
  @Output() onClickSerieProposalItem: EventEmitter<BookableSlot> =
    new EventEmitter()

  @Output() onChangeDates: EventEmitter<{
    startDate: Date
    endDate: Date
  }> = new EventEmitter()

  public scheduleSlots: ScheduleSlot[] = []
  public scheduleSlotsByDayAndGroup$: Observable<any[]>
  public productEntities$: Observable<Dictionary<Product>>

  public productEntities: Dictionary<Product>

  public currentStartDate: Date
  public currentEndDate: Date

  constructor(
    private translate: TranslateService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.setDateRange(moment().startOf('isoWeek').toDate())

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.productEntities$
      .pipe(untilDestroyed(this))
      .subscribe((productEntities) => {
        this.productEntities = productEntities
      })
    this.scheduleSlotsByDayAndGroup$ = combineLatest([
      this.bookableSlots$,
      this.store.select(translationSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ]).pipe(
      untilDestroyed(this),
      withLatestFrom(this.latestBookableSlots$),
      map(
        ([
          [bookableSlots, translations, selectedLanguage],
          latestBookableSlots
        ]) => {
          if (bookableSlots.length == 0) {
            bookableSlots = JSON.parse(JSON.stringify(latestBookableSlots))
          }
          let copyOfBookableSlots
          this.scheduleSlots = this.generateSlots()
          let mappedSlots: any = this.mapScheduleSlotsToViewModel(bookableSlots)

          let restOfProductGroup: any = []
          for (const slot of mappedSlots) {
            for (const slotByGroup of slot.slotsByGroup) {
              if (this.targetedProductsIds.includes(slotByGroup.name)) {
                copyOfBookableSlots = JSON.parse(
                  JSON.stringify(slotByGroup.scheduleSlots)
                )
                copyOfBookableSlots = copyOfBookableSlots.map((item) => {
                  return { ...item, bookableSlots: [] }
                })
                this.targetedProductsIds = this.targetedProductsIds.filter(
                  (item) => item != slotByGroup.name
                )
              }
            }
          }
          for (const targetedProductsId of this.targetedProductsIds) {
            restOfProductGroup.push({
              courseId: null,
              description: this.productEntities[targetedProductsId].description,
              name: targetedProductsId,
              scheduleSlots: copyOfBookableSlots
            })
          }
          const totalSlotsByGroups = [
            ...(mappedSlots[0] ? mappedSlots[0].slotsByGroup : []),
            ...restOfProductGroup
          ]
          if (mappedSlots[0]) {
            mappedSlots[0].slotsByGroup = totalSlotsByGroups.sort((a, b) =>
              a?.description < b?.description ? -1 : 1
            )
          }

          return mappedSlots.map((slots) => {
            for (let slotGroup of slots.slotsByGroup) {
              if (slotGroup.courseId) {
                const translation = translations.find(
                  (t) =>
                    t.courseId == slotGroup.courseId &&
                    t.language == selectedLanguage
                ).description
                if (translation.length) {
                  slotGroup.name = translation
                }
              }
            }

            return slots
          })
        }
      ),

      map((slots) => [
        {
          dayDateTime: slots[0]?.dayDateTime,
          slotsByGroup: this.concatArrays(
            slots.map((slot) => {
              return slot.slotsByGroup
            })
          ).sort((a, b) =>
            a.description.toLowerCase() > b.description.toLowerCase() ? 1 : -1
          )
        }
      ])
    )
  }

  public onBookableSlotClick(bookableSlot) {
    this.onClickBookableSlot.emit(bookableSlot)
  }

  public onSerieProposalItemClick(bookableSlotWithSerieProposalItemId) {
    this.onClickSerieProposalItem.emit(bookableSlotWithSerieProposalItemId)
  }

  private mapScheduleSlotsToViewModel(bookableSlots: BookableSlot[]) {
    const bookableSlotsByDay = Object.values(
      groupBy(bookableSlots, (slot) =>
        moment(slot.startDate).startOf('day').toISOString()
      )
    )

    const bookableSlotsByDayByGroup = bookableSlotsByDay.map((slotDay) =>
      Object.values(groupBy(slotDay, (slot) => slot.bookableProductId))
    )

    return bookableSlotsByDayByGroup.map((bookableSlotsByDay) => {
      const dayDateTime = moment(bookableSlotsByDay[0][0].startDate).startOf(
        'day'
      )

      return {
        dayDateTime,
        slotsByGroup: bookableSlotsByDay
          .map((bookableSlotGroup: any[]) => {
            return {
              name: bookableSlotGroup[0].bookableProductId,
              description:
                this.productEntities[bookableSlotGroup[0].bookableProductId]
                  ?.description,
              courseId: bookableSlotGroup[0]?.courseId,
              scheduleSlots: this.scheduleSlots.map((scheduleSlot) => {
                const scheduleSlotStart = moment(
                  `${dayDateTime.locale('en').format('YYYY-MM-DD')} ${
                    scheduleSlot.fromTime
                  }`
                ).toDate()
                const scheduleSlotEnd = moment(
                  `${dayDateTime.locale('en').format('YYYY-MM-DD')} ${
                    scheduleSlot.tillTime
                  }`
                ).toDate()

                return {
                  ...scheduleSlot,
                  scheduleSlotStart,
                  scheduleSlotEnd,
                  // filter content slots that overlap / fit into the scheduleSlot
                  bookableSlots: bookableSlotGroup
                    .filter(
                      (bookableSlot) =>
                        new Date(bookableSlot.startDate).getTime() <
                          scheduleSlotEnd.getTime() &&
                        new Date(bookableSlot.endDate).getTime() >
                          scheduleSlotStart.getTime()
                    )
                    .map((bookableSlot) => {
                      return {
                        ...bookableSlot,
                        isStart:
                          new Date(bookableSlot.startDate).getTime() >=
                          scheduleSlotStart.getTime(),
                        isEnd:
                          new Date(bookableSlot.endDate).getTime() <=
                            scheduleSlotEnd.getTime() ||
                          (moment(scheduleSlotEnd).format('HH:mm') == '23:45' &&
                            moment(bookableSlot.endDate).format('HH:mm') >=
                              '23:45')
                      }
                    })
                    ?.sort((a, b) =>
                      b.serieProposalItemId < a.serieProposalItemId ? -1 : 1
                    )
                }
              })
            }
          })
          // .sort((a, b) => (a?.name.localeCompare(b?.name) ? -1 : 1))
          .sort((a, b) => (a?.description < b?.description ? -1 : 1))
      }
    })
  }
  private setDateRange(date: Date) {
    this.currentStartDate = moment(date).startOf('isoWeek').toDate()
    this.currentEndDate = moment(date).endOf('isoWeek').toDate()

    this.onChangeDates.emit({
      startDate: this.currentStartDate,
      endDate: this.currentEndDate
    })
  }

  private generateSlots(): ScheduleSlot[] {
    let startTime = moment(this.config.openTime, 'HH:mm')
    const endTime = moment(this.config.closeTime, 'HH:mm')

    let slots: ScheduleSlot[] = []

    while (startTime < endTime) {
      slots.push({
        title: startTime.format('HH:mm'),
        fromTime: startTime.format('HH:mm'),
        tillTime: startTime
          .add(this.config.slotInterval, 'minutes')
          .format('HH:mm')
      })
    }

    return slots
  }
  private concatArrays(array: any[][]): any[] {
    return array.reduce(
      (result, currentArray) => result.concat(currentArray),
      []
    )
  }
}
